import React from 'react';
import { Icon, Text, Tooltip } from '@shopify/polaris';

/**
 * Shared surface pieces for the admin's content pages.
 *
 * Colours are Polaris 13 tokens by value, because these are hand-drawn spans rather
 * than Polaris components.
 *
 * NOTE ON ICONS: Polaris's .Polaris-Icon carries `margin: auto`. A bare <Icon> used
 * as a direct flex child soaks up all the free space and shunts whatever follows it
 * to the far end of the row. The Icon here is centred inside a fixed, flex-none disc
 * - do not "simplify" that away.
 */

export const TINT = {
  info: { bg: '#eaf4ff', fg: '#003a5a' },
  success: { bg: '#cdfee1', fg: '#0c5132' },
  warning: { bg: '#fff1e3', fg: '#5e4200' },
  critical: { bg: '#fee9e8', fg: '#8e1f0b' },
  magic: { bg: '#f8f7ff', fg: '#5700d1' },
};

/** Icon on a tinted disc. */
export function IconDisc({ icon, tone = 'info', size = 32 }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        width: size,
        height: size,
        borderRadius: '10px',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        background: (TINT[tone] || TINT.info).bg,
      }}
    >
      <Icon source={icon} tone={TINT[tone] ? tone : 'base'} />
    </span>
  );
}

/**
 * Icon in an OUTLINED frame, rather than on a tinted disc.
 *
 * The tinted disc (IconDisc) colour-codes a card by tone. The headline metric
 * cards deliberately don't: three saturated swatches in a row is the loudest
 * thing on the dashboard, and it is decoration - the tint carries no meaning a
 * merchant can read. A neutral frame lets the number be the loudest thing
 * instead, and leaves the one spot of colour on the badge, which does say
 * something.
 *
 * Same single-sized-box shape as IconDisc, for the .Polaris-Icon margin reason
 * in the file header.
 */
function IconFrame({ icon, size = 52 }) {
  return (
    <span
      // .zs-stat-icon is what enlarges the glyph to 24px - Polaris pins
      // .Polaris-Icon to 20px and has no size prop. See resources/css/app.css.
      className="zs-stat-icon"
      style={{
        display: 'inline-flex', width: size, height: size, borderRadius: '14px',
        alignItems: 'center', justifyContent: 'center', flex: 'none',
        // Outline only, no fill. The border alone is enough to frame the glyph at
        // 24px, and it matches the card's own #e3e3e3 edge so the frame reads as
        // part of the card rather than a grey chip sitting on top of it.
        background: 'transparent', border: '1px solid #e3e3e3',
      }}
    >
      <Icon source={icon} tone="base" />
    </span>
  );
}

/**
 * The one spot of colour on a MetricCard, so keep it for something worth reading.
 *
 * With `onClick` it renders as a button instead of a span. That is for the case
 * where the state it reports is one the merchant can do something about - a badge
 * that says a limit is reached and then offers no way past it is a dead end.
 */
function StatBadge({ children, tone, onClick, title }) {
  const palette = tone && TINT[tone] ? TINT[tone] : { bg: '#eaf2fe', fg: '#1c4f9c' };

  const base = {
    display: 'inline-flex', alignItems: 'center', gap: '4px', flex: 'none',
    background: palette.bg, color: palette.fg,
    fontSize: '12px', fontWeight: 600, lineHeight: 1.4,
    padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap',
    // Nudged down so the pill's text baseline sits with the label's, rather
    // than its box top-aligning with the label's box.
    marginTop: '2px',
  };

  if (!onClick) return <span style={base}>{children}</span>;

  return (
    <button
      type="button"
      onClick={onClick}
      // The state itself, so the button label can be the action rather than a
      // restatement of what the value and the bar already say.
      title={title}
      aria-label={title ? `${title} \u2013 ${children}` : undefined}
      style={{ ...base, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
    >
      {children}
      <span aria-hidden="true">{'\u2192'}</span>
    </button>
  );
}

/**
 * Usage bar for a MetricCard footer.
 *
 * The colour is state, not decoration - a merchant on 6 of 7 seats should see
 * they are nearly out without reading the number. It is deliberately NOT the
 * only signal: the badge beside it says "1 still available" or "Upgrade plan"
 * in words, so the state survives colour blindness and greyscale printing.
 *
 * No critical red at 100%. In Shopify's language red means an error or a
 * destructive action, and a full seat allowance is neither - it is a ceiling
 * the merchant can lift. A bar that is visibly full already says full; the
 * colour only has to say "attention", which is what the amber does.
 */
export function ProgressTrack({ percent }) {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const fill = pct >= 80 ? '#b98900' : '#008060';

  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        marginTop: '16px', height: '6px', borderRadius: '999px',
        background: '#ebebeb', overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: pct + '%', height: '100%', borderRadius: '999px',
          background: fill, transition: 'width 200ms ease',
        }}
      />
    </div>
  );
}

/**
 * Headline metric card: framed icon, label, big value, optional badge.
 *
 * Layout is a single flex row with the badge as its last child rather than an
 * absolutely positioned corner element - so a long value or a translated label
 * pushes the badge instead of sliding underneath it. German and Dutch labels
 * run about half again as long as the English.
 *
 * `badge` is optional by design: a badge that only restates the label is noise,
 * so a card with nothing true to add in that corner simply omits it.
 */
export function MetricCard({ icon, label, value, badge, badgeTone, badgeOnClick, badgeTitle, tooltip, footer }) {
  const valueMarkup = (
    <div
      style={{
        marginTop: '3px', fontSize: '32px', fontWeight: 700, color: '#1a1a1a',
        // Tight tracking and a 1.05 leading keep a four-figure value on one
        // line and stop the number drifting away from its label.
        letterSpacing: '-0.9px', lineHeight: 1.05,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
  );

  return (
    // SURFACE's own 12px, not a 16px override. Everything below this row -
    // the two panels, Recent Activity and the three support cards (Tailwind
    // rounded-xl, which is 12px) - is 12px, so the override was what made the
    // metric row read as a separate system from the page under it.
    <div className="zs-stat-card" style={{ ...SURFACE, padding: '20px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
          <IconFrame icon={icon} />
          <div style={{ minWidth: 0 }}>
            {/* Slightly lighter than the value so the number leads. Both were
                near-black before, which left the label competing with it. */}
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#4a4a4a', lineHeight: 1.3 }}>
              {label}
            </div>
            {tooltip ? <Tooltip content={tooltip}>{valueMarkup}</Tooltip> : valueMarkup}
          </div>
        </div>
        {badge ? (
          <StatBadge tone={badgeTone} onClick={badgeOnClick} title={badgeTitle}>
            {badge}
          </StatBadge>
        ) : null}
      </div>
      {/* Optional slot under the row - a usage bar, for instance. Cards without
          one keep their original height, so a row of mixed cards still aligns
          on the grid rather than one of them growing on its own. */}
      {footer || null}
    </div>
  );
}

export const SURFACE = {
  background: '#ffffff',
  border: '1px solid #e3e3e3',
  borderRadius: '12px',
  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
};

/**
 * Card header band: disc, title, optional context beside it, and whatever
 * controls shape the contents on the right.
 */
// `pad` is optional and defaults to the original value, so every other card
// header in the app is untouched. The dashboard's week chart passes it.
export function CardHeader({ icon, tone = 'info', title, context, actions, pad = '10px 16px' }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '12px', flexWrap: 'wrap', padding: pad,
        borderBottom: '1px solid #ebebeb',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <IconDisc icon={icon} tone={tone} size={26} />
        <Text as="h3" variant="headingSm">{title}</Text>
        {context ? (
          <Text as="span" variant="bodySm" tone="subdued">{context}</Text>
        ) : null}
      </div>
      {actions ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export default IconDisc;

/**
 * Table column heading with a muted icon.
 *
 * The Shopify app defines this inline in four separate files; it lives here once
 * instead. Note that an IndexTable heading whose title is a ReactNode rather than
 * a string MUST also carry an explicit `id`, or Polaris cannot key the column.
 */
export function ColumnHeading({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {/* Sized wrapper: .Polaris-Icon carries margin:auto and would otherwise
          swallow the row's free space as a bare flex child. */}
      <span className="w-4 h-4 shrink-0 opacity-70">
        <Icon source={icon} tone="subdued" />
      </span>
      {children}
    </span>
  );
}
